import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { BRAND } from "@/lib/data";
import Logo from "@/components/shared/Logo";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/categories", label: "Categories" },
  { to: "/cities", label: "Cities" },
  { to: "/pricing-guide", label: "Pricing" },
  { to: "/pickup-guide", label: "Pick-up" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${
          scrolled
            ? "bg-white/95 backdrop-blur-lg border-b border-line shadow-[0_1px_20px_-6px_rgba(11,37,69,0.15)]"
            : "bg-white/95 backdrop-blur border-b border-line/50"
        }`}
        data-testid="site-header"
      >
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 h-[72px] md:h-[88px] flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <Logo className="text-[22px] md:text-[26px]" tone="navy" />
          </Link>

          <nav className="hidden xl:flex items-center gap-7">
            {NAV.map((n) => (
              <NavLink
                key={n.to} to={n.to} end={n.to === "/"}
                className={({ isActive }) =>
                  `relative text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                    isActive ? "text-[#0B2545]" : "text-[#5B6472] hover:text-[#0B2545]"
                  }`
                }
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              >
                {({ isActive }) => (
                  <span className="inline-flex items-center gap-2">
                    {n.label}
                    {isActive && <span className="h-1 w-1 rounded-full bg-[#0B2545]" />}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={BRAND.phoneHref}
              className="hidden md:inline-flex items-center gap-3 bg-[#0B2545] text-white px-6 py-3.5 md:py-4 hover:bg-[#13366B] transition-colors duration-300"
              data-testid="header-call"
            >
              <Phone className="h-5 w-5" strokeWidth={2} />
              <span className="font-mono text-[16px] md:text-[17px] tracking-wider font-semibold">{BRAND.phone}</span>
            </a>
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="xl:hidden inline-flex items-center justify-center border border-[#0B2545] h-10 w-10 hover:bg-[#0B2545] hover:text-white transition-colors text-[#0B2545]"
              aria-label="Open menu"
              data-testid="menu-open"
            >
              <Menu className="h-4 w-4" strokeWidth={1.8} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-white xl:hidden overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-5 h-[72px] border-b border-line">
              <Logo className="text-[22px]" tone="navy" />
              <button
                type="button" onClick={() => setOpen(false)}
                className="border border-[#0B2545] h-10 w-10 inline-flex items-center justify-center text-[#0B2545]"
                aria-label="Close menu" data-testid="menu-close"
              >
                <X className="h-4 w-4" strokeWidth={1.8} />
              </button>
            </div>
            <ul className="px-5 py-6 space-y-1">
              {NAV.map((n, i) => (
                <motion.li
                  key={n.to}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.04 * i + 0.06, duration: 0.5 }}
                >
                  <Link
                    to={n.to}
                    className="block py-4 border-b border-line font-serif text-3xl text-[#0B2545]"
                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  >
                    <span className="overline mr-4">{String(i + 1).padStart(2, "0")}</span>
                    {n.label}
                  </Link>
                </motion.li>
              ))}
            </ul>
            <div className="px-5 pb-24 space-y-3 pt-4">
              <a href={BRAND.phoneHref} className="btn-primary w-full justify-center">
                <Phone className="h-4 w-4" strokeWidth={1.8} />
                {BRAND.phone}
              </a>
              <a href={BRAND.emailHref} className="btn-ghost-light w-full justify-center">
                {BRAND.email}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
